import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FeatureCollection } from "geojson";

import { api } from "../utils/api";
import { ApiResponse } from "../types/api";

export type RealtimeKind =
  | "hotspot"
  | "lightning"
  | "hazard"
  | "ignition"
  | "burnt_area";
/** Kinds a user can place by hand (a simulated hazard would be
 *  indistinguishable from an official agency warning, so hazards are not
 *  among them). */
export type SimulatableKind = "hotspot" | "lightning";
export type RealtimeWindow = "1h" | "6h" | "24h" | "72h";

export interface RealtimeBbox {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

// Round the bbox outward to ~2 decimal places (~1 km) so tiny viewport moves
// don't change the query key (and thus don't trigger a refetch), while the
// requested area always covers the true viewport.
export const roundBboxOutward = (bbox: RealtimeBbox): RealtimeBbox => ({
  top: Math.ceil(bbox.top * 100) / 100,
  right: Math.ceil(bbox.right * 100) / 100,
  bottom: Math.floor(bbox.bottom * 100) / 100,
  left: Math.floor(bbox.left * 100) / 100,
});

export const useRealtimeObservations = (
  kind: RealtimeKind,
  bbox: RealtimeBbox | null,
  windowHours: RealtimeWindow,
  enabled: boolean
) => {
  const rounded = bbox ? roundBboxOutward(bbox) : null;

  return useQuery<FeatureCollection>({
    queryKey: [
      "realtime-observations",
      kind,
      windowHours,
      rounded?.top,
      rounded?.left,
      rounded?.bottom,
      rounded?.right,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        kind,
        window: windowHours,
        top: String(rounded!.top),
        left: String(rounded!.left),
        bottom: String(rounded!.bottom),
        right: String(rounded!.right),
      });
      const response = await api.get<ApiResponse<FeatureCollection>>(
        `/api/realtime/observations?${params.toString()}`
      );
      return response.data;
    },
    enabled: enabled && !!rounded,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};

export interface SimulateDetectionPayload {
  kind: SimulatableKind;
  lat: number;
  lon: number;
  /** How long the simulated detection lives before it disappears. */
  lifespan_minutes: number;
  /** Kind-specific overrides (hotspot: frp/confidence/brightness;
   *  lightning: polarity/amplitude_ka/strike_type). Whitelisted server-side. */
  properties?: Record<string, unknown>;
}

export interface SimulatedDetectionResult {
  id: number;
  kind: SimulatableKind;
  observed_at: string;
  expires_at: string;
}

/**
 * Place a simulated detection. It is stored server-side against a hidden
 * per-tenant "Simulated" source, so it appears for every viewer of the layer
 * (and runs through realtime notifications) until its lifespan elapses.
 */
export const useSimulateDetection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SimulateDetectionPayload) => {
      const response = await api.post<ApiResponse<SimulatedDetectionResult>>(
        "/api/realtime/observations/simulated",
        payload
      );
      return response.data;
    },
    onSuccess: (result) => {
      // Refetch the affected kind's layer immediately rather than waiting for
      // the 60s poll — the whole point is seeing the detection appear.
      queryClient.invalidateQueries({
        queryKey: ["realtime-observations", result.kind],
      });
    },
  });
};

export const useDeleteSimulatedDetection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number; kind: RealtimeKind }) => {
      await api.delete(`/api/realtime/observations/simulated/${id}`);
    },
    onSuccess: (_data, { kind }) => {
      queryClient.invalidateQueries({
        queryKey: ["realtime-observations", kind],
      });
    },
  });
};
